import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'

export const Route = createFileRoute('/_site/compare/betterstack')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Better Stack Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Better Stack: per-responder pricing vs flat plans. Uptrack checks every 30s from $3/mo with alert confirmation. Compare features." },
      { property: 'og:title', content: 'Better Stack Alternative — Uptrack' },
      { property: 'og:description', content: "Better Stack prices uptime per responder, plus monitor packs. Uptrack has flat plans from $3/mo. Compare features and pricing." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-betterstack.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/betterstack' },
    ],
    scripts: comparisonScripts('betterstack', "Better Stack"),
  }),
})

const data: ComparisonData = {
  competitor: "Better Stack",
  tagline: "Better Stack combines uptime monitoring, on-call, logs, and metrics in one platform. Uptrack focuses on uptime, browser checks, and status pages, with flat plans from $3/mo.",
  heroDescription: "These facts come from Better Stack's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Free plan", uptrack: "5 monitors at 30 s from Nuremberg", competitor: "10 monitors at 3 min, 10 heartbeats, 1 status page" },
    { feature: "Paid entry price", uptrack: "$3/mo (Pro, 50 monitors)", competitor: "$34/mo per responder ($29 billed annually)", uptrackWins: true },
    { feature: "Extra monitors", uptrack: "Team: 200 monitors for $20/mo", competitor: "$25/mo per 50 monitors ($21 billed annually)", uptrackWins: true },
    { feature: "Fastest check interval", uptrack: "30 sec", competitor: "30 sec" },
    { feature: "Down confirmation", uptrack: "2-5 consecutive failed checks; several check locations must confirm", competitor: "At least 3 of 4 locations must fail (default)" },
    { feature: "Status page custom domain", uptrack: "Team and Business", competitor: "Included on the base paid plan" },
    { feature: "On-call scheduling", uptrack: "Rotations and escalation policies", competitor: "Full on-call scheduling" },
    { feature: "Logs and metrics", uptrack: "Not included", competitor: "Available (billed separately)" },
  ],

  painPoints: [
    {
      title: "Per-responder pricing",
      description: "Better Stack's paid uptime plan costs $34/mo per responder ($29/mo billed annually), and extra monitors cost $25/mo per 50. Uptrack plans are flat: Team is $20/mo for 200 monitors and 5 team members.",
    },
    {
      title: "A broader platform",
      description: "Better Stack also covers logs, metrics, and on-call. If you only need uptime checks, browser checks, and status pages, Uptrack covers those in a smaller product.",
    },
    {
      title: "Free plan size",
      description: "Better Stack's free plan includes 10 monitors and 1 status page. Uptrack's free plan has 5 monitors at 30-second checks from one location. Pro is $3/mo for 50 monitors at 30-second checks, with a 30-day money-back guarantee.",
    },
  ],

  differentiators: [
    "Flat pricing: $3/mo for 50 monitors at 30-second checks. No per-responder fees and no usage-based charges.",
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Focused tool: uptime, browser, cron/heartbeat monitoring, alerting, and status pages. No log or metrics pipelines to set up.",
    "Escalation and on-call rotations: multi-step escalation with configurable delays, plus daily, weekly, or custom rotations with overrides. A step can page whoever is on call now.",
  ],

  notForYou: [
    "If you need integrated logs, metrics, and traces alongside monitoring, Better Stack's unified platform covers all of them.",
    "If you need advanced on-call shift management, such as layered or follow-the-sun schedules, shift swaps, or a mobile push app, Better Stack's on-call covers more. Uptrack covers rotations and overrides.",
    "If your team already uses Better Stack for observability and wants monitoring in the same dashboard.",
    "If you need phone call and SMS alerts included, Better Stack includes unlimited calls and SMS on paid plans.",
    "If you need more than 5 free monitors, Better Stack offers 10 monitors and 1 status page. Uptrack's free plan is 5 monitors at 30 seconds from one location.",
  ],
}
